import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ivrNodes } from '@/api/ivrNodes';
import type { IvrNodeRow } from '@/api/ivrNodes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { NodeTypeBadge } from '@/components/ivr/NodeTypeBadge';
import { isMenuLike } from '@/components/ivr/list/AddChildPopover';
import { formatRef } from '@/lib/formatRef';

interface Props {
  nodeId: string;
  nodeName: string;
  treeId: string;
  treeNodes: IvrNodeRow[];
  currentParentId?: string | null;
  onClose: () => void;
  onSaved: () => void;
}

const NO_PARENT = '__none__';

export function ChangeParentModal({
  nodeId,
  nodeName,
  treeId,
  treeNodes,
  currentParentId,
  onClose,
  onSaved,
}: Props) {
  const qc = useQueryClient();
  const [parentId, setParentId] = useState<string>(currentParentId ?? NO_PARENT);
  const [branchKey, setBranchKey] = useState('next');
  const [error, setError] = useState('');
  // Sam 2026-05-26: dropdown defaults to menu-like types only. Most users
  // don't want to chain a node under a playback / transfer / dial etc.
  const [showAllTypes, setShowAllTypes] = useState(false);

  const reparent = useMutation({
    mutationFn: () =>
      ivrNodes.reparent(nodeId, {
        parent_node_id: parentId === NO_PARENT ? null : parentId,
        branch_key: branchKey.trim() || 'next',
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['ivrEdgesForTree', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrEdges', treeId] });
      qc.invalidateQueries({ queryKey: ['ivrNodes', treeId] });
      onSaved();
      onClose();
    },
    onError: (e: unknown) => {
      const err = e as { code?: string };
      if (err?.code === 'nodes_different_trees') {
        setError('Cannot assign a parent from a different tree.');
      } else if (err?.code === 'self_parent') {
        setError('A node cannot be its own parent.');
      } else if (err?.code === 'edge_branch_key_conflict') {
        setError('That branch key is already used on the selected parent.');
      } else {
        setError('Failed to reparent node. Please try again.');
      }
    },
  });

  // Eligible parents: by default ONLY menu-like types (menu,
  // playback_settings). The "Show all node types" toggle widens it to
  // every other node, for cases like playback→playback chains.
  const eligibleParents = treeNodes.filter((n) => {
    if (n.id === nodeId) return false;
    if (showAllTypes) return true;
    return isMenuLike(n.node_type);
  });
  const hiddenCount = treeNodes.filter((n) => n.id !== nodeId && !isMenuLike(n.node_type)).length;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="text-base">Change parent</DialogTitle>
          <p className="text-sm text-slate-500 mt-0.5">
            Reassigning parent for <span className="font-medium text-slate-700">{nodeName}</span>.
            All current incoming edges will be removed.
          </p>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {/* Parent picker */}
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-slate-600">Parent node</Label>
            <Select value={parentId} onValueChange={setParentId}>
              <SelectTrigger className="h-9 text-sm">
                <SelectValue placeholder="None (root node)" />
              </SelectTrigger>
              <SelectContent className="max-h-60">
                <SelectItem value={NO_PARENT}>
                  <span className="text-slate-500 italic text-sm">None — make root / orphan</span>
                </SelectItem>
                {eligibleParents.map((n) => (
                  <SelectItem key={n.id} value={n.id}>
                    <span className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-slate-400 shrink-0">{formatRef(n.ref_number)}</span>
                      <span className="truncate max-w-[180px] text-sm">{n.name}</span>
                      <NodeTypeBadge type={n.node_type} className="shrink-0" />
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!showAllTypes && hiddenCount > 0 && (
              <button
                type="button"
                onClick={() => setShowAllTypes(true)}
                className="text-[11px] text-indigo-600 hover:underline"
              >
                Show all node types ({hiddenCount} more) — playbacks, voicemails, etc.
              </button>
            )}
            {showAllTypes && (
              <button
                type="button"
                onClick={() => setShowAllTypes(false)}
                className="text-[11px] text-slate-500 hover:underline"
              >
                Show only menu-type parents
              </button>
            )}
          </div>

          {/* Branch key — ALWAYS visible (was previously hidden when
              parent was "None"). Disabled in that case since a branch
              key without a parent isn't meaningful. */}
          <div className="space-y-1.5">
            <Label htmlFor="cp-bk" className="text-xs font-medium text-slate-600">
              Branch key
            </Label>
            <Input
              id="cp-bk"
              value={branchKey}
              onChange={(e) => setBranchKey(e.target.value)}
              placeholder="next, 1, 2, timeout…"
              className="h-9 text-sm font-mono"
              maxLength={32}
              disabled={parentId === NO_PARENT}
            />
            <p className="text-[11px] text-slate-400">
              {parentId === NO_PARENT
                ? 'Pick a parent first — a branch key needs a parent to attach to.'
                : 'Which branch from the parent leads to this node.'}
            </p>
          </div>

          {error && (
            <p role="alert" className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {error}
            </p>
          )}
        </div>

        <DialogFooter className="gap-2">
          <DialogClose asChild>
            <Button variant="outline" size="sm" onClick={onClose}>Cancel</Button>
          </DialogClose>
          <Button
            size="sm"
            onClick={() => reparent.mutate()}
            disabled={reparent.isPending}
          >
            {reparent.isPending ? 'Saving…' : 'Save parent'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
